import { useState, useEffect } from "react";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { apiFetch } from "@/lib/api";
import { useInitialData } from "@/lib/initial-data-context";
import type { GalleryImage } from "@/lib/initial-data";

export default function Gallery() {
  const { galleryImages: initialImages } = useInitialData();

  const [images, setImages] = useState<GalleryImage[]>(initialImages ?? []);
  const [loading, setLoading] = useState(!initialImages);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    if (initialImages) return;
    apiFetch("/gallery/images")
      .then(setImages)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function getImageUrl(imagePath: string) {
    return `${BASE}/api/storage${imagePath}`;
  }

  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Photo Gallery | Our Furry Friends"
        description="Browse photos of the happy pets we care for at Tallahassee Lassie Pet Care. See our professional dog walkers and pet sitters in action with their furry friends in Tallahassee."
        path="/gallery"
      />
      <div className="container mx-auto px-4">
        <Reveal>
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6">Our Gallery</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Snapshots of the pets and moments that make our work so rewarding.
            </p>
          </div>
        </Reveal>

        {loading ? (
          <p className="text-center text-muted-foreground">Loading gallery...</p>
        ) : images.length === 0 ? (
          <Reveal>
            <div className="bg-primary/5 border border-primary/10 p-12 text-center max-w-2xl mx-auto" style={{ borderRadius: "32px 0 32px 0" }}>
              <p className="text-6xl mb-6">📸</p>
              <h2 className="text-2xl font-serif font-bold mb-4">Photos Coming Soon</h2>
              <p className="text-lg text-muted-foreground">
                We're putting together a gallery of all the wonderful pets we work with. Check back soon!
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image) => (
              <Reveal key={image.id}>
                <button
                  type="button"
                  className="aspect-square w-full overflow-hidden cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  style={{ borderRadius: "16px 0 16px 0" }}
                  onClick={() => setSelectedImage(image)}
                  aria-label={`Open ${image.title || "gallery photo"}`}
                >
                  <img
                    src={getImageUrl(image.imagePath)}
                    alt={image.title || "Gallery photo"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(selectedImage.imagePath)}
              alt={selectedImage.title || "Gallery photo"}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            {(selectedImage.title || selectedImage.caption) && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-4 rounded-b-lg">
                {selectedImage.title && <p className="font-semibold">{selectedImage.title}</p>}
                {selectedImage.caption && <p className="text-sm text-white/80">{selectedImage.caption}</p>}
              </div>
            )}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 bg-white/20 hover:bg-white/40 text-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
